//@flow
import React, {useState} from 'react';

import {
  Button,
  ButtonGroup,
  Typography,
  makeStyles,
  Link,
} from '@material-ui/core';
import {DeleteForever} from '@material-ui/icons';
import SaveAltRoundedIcon from '@material-ui/icons/SaveAltRounded';
import CodeRoundedIcon from '@material-ui/icons/CodeRounded';
import {useOrderTaxInvoiceOrderActions} from '../../utils';
import {colors} from '../../../../assets/styles/colors';

import type {
  TaxInvoiceDocument,
  TaxInvoiceStateType,
} from '../../../../entities';
import type {Theme} from '@material-ui/core';
import ConfirmModal from '../../../../elements/Modals/ConfirmModal';

type Props = {
  document: TaxInvoiceDocument,
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.75rem',
    width: '100%',
    border: `1px solid ${colors.grey300}`,
    borderRadius: '1rem',
    overflow: 'hidden',
    position: 'relative',

    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '4px',
      height: '100%',
      borderRadius: '4px 0 0 4px',
    },

    '&[data-state="PROCESS"]::before': {
      background: colors.warning,
    },
    '&[data-state="EMITTED"]::before': {
      background: colors.success,
    },
    '&[data-state="CANCELLED"]::before': {
      background: colors.error,
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '.5rem 1rem',
  },
  receiptDocument: {
    fontSize: '.875rem',
    background: colors.grey200,
    color: colors.grey600,
    padding: '.25rem .5rem',
    borderRadius: '.25rem',
  },
  eInvoiceState: {
    fontSize: '.875rem',
    fontWeight: 600,
    '&[data-state="SCHEDULED"]': {
      color: colors.warning,
    },
    '&[data-state="ACCEPTED"]': {
      color: colors.success,
    },

    '&[data-state="CANCELED"], &[data-state="REJECTED"], &[data-state="FAILED"]': {
      color: colors.error,
    },

    '&[data-state="PENDING"]': {
      color: colors.info,
    },
  },
  body: {
    padding: '0 1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '.25rem',
    position: 'relative',
  },
  orderNumber: {
    fontSize: '.875rem',
    color: colors.grey600,
  },
  clientName: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.grey900,
  },
  orderAmount: {
    fontSize: '.875rem',
    color: colors.grey900,
  },
  orderOrigin: {
    fontSize: '.875rem',
    background: colors.grey200,
    color: colors.grey600,
    padding: '.25rem .5rem',
    borderRadius: '.25rem',
    position: 'absolute',
    right: '.5rem',
    bottom: '.5rem',
  },
  footer: {
    width: '100%',
    borderTop: `1px solid ${colors.grey300}`,

    '& .MuiButton-label': {
      textTransform: 'none',
      fontSize: '.875rem',
    },
  },
  cancelButton: {
    color: colors.error,
  },
}));

const CAN_CANCEL_TAX: TaxInvoiceStateType[] = ['ACCEPTED', 'SCHEDULED'];

export default function TaxInvoiceCard({document}: Props) {
  const classes = useStyles();

  const {cancelOrderTaxInvoice} = useOrderTaxInvoiceOrderActions();

  const [taxOrderId, setTaxOrderId] = useState<string | typeof undefined>(
    undefined
  );

  return (
    <aside className={classes.root} data-state={document.state.id}>
      <header className={classes.header}>
        <div className={classes.receiptDocument}>
          {document?.documentType?.label} | {document?.documentType?.code}
        </div>

        <span className={classes.eInvoiceState} data-state={document.state.id}>
          {document.state.label}
        </span>
      </header>

      <div className={classes.body}>
        <Typography className={classes.orderNumber}>
          <Link
            href={`/admin/orders/${document.orderId}`}
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
          >
            N° Orden {document.orderId}
          </Link>
        </Typography>
        {document.formattedExternalReferenceId && (
          <Typography className={classes.orderNumber}>
            N° {document.formattedExternalReferenceId}
          </Typography>
        )}
        <Typography className={classes.clientName}>
          {document.clientName}
        </Typography>
        <Typography className={classes.orderAmount}>
          {document.orderAmount}
        </Typography>

        <span className={classes.orderOrigin}>
          {document.isManualOrder ? 'MANUAL' : 'WEB'}
        </span>
      </div>

      <ConfirmModal
        message={
          document.canBeCancelledWithoutCreditNote
            ? 'Se anulara la creaación del documento'
            : 'Se va a crear una Nota de Crédito para anular el documento'
        }
        open={!!taxOrderId}
        onCancel={() => setTaxOrderId(undefined)}
        onClose={() => setTaxOrderId(undefined)}
        onConfirm={() => {
          cancelOrderTaxInvoice(document.orderTaxInvoiceId);
          setTaxOrderId(undefined);
        }}
      />

      <footer className={classes.footer}>
        <ButtonGroup variant="text" fullWidth>
          <Button
            color="default"
            startIcon={<DeleteForever />}
            onClick={() => {
              setTaxOrderId(document.orderId);
            }}
            className={classes.cancelButton}
            disabled={!CAN_CANCEL_TAX.includes(document.state.id)}
          >
            Cancelar
          </Button>
          <Button
            color="primary"
            href={document.pdfUrl}
            rel="noopener noreferrer"
            target="_blank"
            disabled={!document.pdfUrl}
            startIcon={<SaveAltRoundedIcon />}
          >
            PDF
          </Button>
          <Button
            color="primary"
            href={document.xmlUrl}
            rel="noopener noreferrer"
            target="_blank"
            disabled={!document.xmlUrl}
            startIcon={<CodeRoundedIcon />}
          >
            XML
          </Button>
        </ButtonGroup>
      </footer>
    </aside>
  );
}
